// Static data constants for reuse across components

/**
 * GST Unique Quantity Codes (UQC) for e-invoice / e-way bill compliance.
 * `code` is stored on itemUnit / uom; `description` is for display only.
 */
export const GST_UNIT_OPTIONS = [
  { code: 'BAG', description: 'Bags' },
  { code: 'BAL', description: 'Bale' },
  { code: 'BDL', description: 'Bundles' },
  { code: 'BKL', description: 'Buckles' },
  { code: 'BOU', description: 'Billions of units' },
  { code: 'BOX', description: 'Box' },
  { code: 'BTL', description: 'Bottles' },
  { code: 'BUN', description: 'Bunches' },
  { code: 'CAN', description: 'Cans' },
  { code: 'CBM', description: 'Cubic meters' },
  { code: 'CCM', description: 'Cubic centimeters' },
  { code: 'CMS', description: 'Centimeters' },
  { code: 'CTN', description: 'Cartons' },
  { code: 'DOZ', description: 'Dozen' },
  { code: 'DRM', description: 'Drum' },
  { code: 'GGR', description: 'Great gross' },
  { code: 'GMS', description: 'Grams' },
  { code: 'GRS', description: 'Gross' },
  { code: 'GYD', description: 'Gross yards' },
  { code: 'KGS', description: 'Kilograms' },
  { code: 'KLR', description: 'Kilolitres' },
  { code: 'KME', description: 'Kilometres' },
  { code: 'LTR', description: 'Litres' },
  { code: 'MLT', description: 'Millilitres' },
  { code: 'MTR', description: 'Meters' },
  { code: 'MTS', description: 'Metric tons' },
  { code: 'NOS', description: 'Numbers' },
  { code: 'OTH', description: 'Others' },
  { code: 'PAC', description: 'Packs' },
  { code: 'PCS', description: 'Pieces' },
  { code: 'PRS', description: 'Pairs' },
  { code: 'QTL', description: 'Quintal' },
  { code: 'ROL', description: 'Rolls' },
  { code: 'SET', description: 'Sets' },
  { code: 'SQF', description: 'Square feet' },
  { code: 'SQM', description: 'Square meters' },
  { code: 'SQY', description: 'Square yards' },
  { code: 'TBS', description: 'Tablets' },
  { code: 'TGM', description: 'Ten gross' },
  { code: 'THD', description: 'Thousands' },
  { code: 'TON', description: 'Tonnes' },
  { code: 'TUB', description: 'Tubes' },
  { code: 'UGS', description: 'US gallons' },
  { code: 'UNT', description: 'Units' },
  { code: 'YDS', description: 'Yards' },
] as const;

/** GST UQC codes only (stored values) */
export const UNITS = GST_UNIT_OPTIONS.map((u) => u.code);

export const SHAPES = [
  'Round', 'Square', 'Rectangular', 'Hexagonal', 'Flat', 'Tube', 'Angle'
] as const;

export const TYPES = [
  'Goods', 'Services'
] as const;

export const FINISHING_TYPES = [
  'Polished', 'Anodized', 'Coated', 'Heat Treated', 'Galvanized', 'Painted', 'Raw'
] as const;

// Type definitions for better type safety
export type Unit = (typeof GST_UNIT_OPTIONS)[number]['code'];
export type Shape = typeof SHAPES[number];
export type Type = typeof TYPES[number];
export type FinishingType = typeof FINISHING_TYPES[number];

/** Map legacy free-text units to GST UQC codes (for existing master data). */
export const LEGACY_UNIT_TO_GST: Record<string, Unit> = {
  kg: 'KGS',
  kgs: 'KGS',
  kilogram: 'KGS',
  kilograms: 'KGS',
  gram: 'GMS',
  grams: 'GMS',
  gms: 'GMS',
  tonne: 'TON',
  tonnes: 'TON',
  ton: 'TON',
  tons: 'TON',
  mts: 'MTS',
  'metric ton': 'MTS',
  'metric tons': 'MTS',
  piece: 'PCS',
  pieces: 'PCS',
  pcs: 'PCS',
  nos: 'NOS',
  numbers: 'NOS',
  number: 'NOS',
  meter: 'MTR',
  meters: 'MTR',
  metre: 'MTR',
  metres: 'MTR',
  mtr: 'MTR',
  liter: 'LTR',
  liters: 'LTR',
  litre: 'LTR',
  litres: 'LTR',
  ltr: 'LTR',
  'square meter': 'SQM',
  'square meters': 'SQM',
  'square metre': 'SQM',
  'square metres': 'SQM',
  sqm: 'SQM',
  'square feet': 'SQF',
  'square foot': 'SQF',
  sqf: 'SQF',
};

export function toGstUnitCode(value?: string | null): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const upper = raw.toUpperCase();
  if ((UNITS as readonly string[]).includes(upper)) return upper;
  return LEGACY_UNIT_TO_GST[raw.toLowerCase()] ?? raw;
}
// Workers and Machines data for reuse across components
export const PACKING_WORK_AREA_ID = 11;

export const WORKERS = [
  { id: '1', name: 'John Doe' },
  { id: '2', name: 'Jane Smith' },
  { id: '3', name: 'Mike Johnson' },
  { id: '4', name: 'Sarah Wilson' },
  { id: '5', name: 'David Brown' },
  { id: '6', name: 'Lisa Davis' },
  { id: '7', name: 'Robert Miller' },
  { id: '8', name: 'Emily Garcia' }
] as const;

export const MACHINES = [
  { id: '1', name: 'Machine A1' },
  { id: '2', name: 'Machine B2' },
  { id: '3', name: 'Machine C3' },
  { id: '4', name: 'Machine D4' },
  { id: '5', name: 'Machine E5' },
  { id: '6', name: 'Machine F6' }
] as const;

export const SHIFTS = ['MORNING', 'AFTERNOON', 'NIGHT'] as const;

// Type definitions for workers and machines
export type Worker = typeof WORKERS[number];
export type Machine = typeof MACHINES[number];
export type Shift = typeof SHIFTS[number];
